/**
 * PlansComparison — Tabla comparativa expandible bajo los 5 planes (v10.6.3)
 * Muestra límites, add-ons y servicios adicionales para acelerar la decisión.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Minus, Star } from 'lucide-react';

const formatCLP = (n) => n ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n) : '—';
const formatUF = (n) => n != null ? `${new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(n)} UF` : '—';

// Filas de la tabla comparativa — cada una lee del `pricing`/`limites` correspondiente.
const COMPARE_ROWS = [
  { section: 'Precio y facturación' },
  { label: 'Precio', get: (p) => {
      const pr = p.pricing || {};
      if (pr.moneda_principal === 'CLP') return `${formatCLP(pr.precio_mensual_clp)}/mes`;
      if (p.requiere_cotizacion) return pr.mostrar_desde ? `Desde ${formatUF(pr.precio_anual_uf)}/año` : formatUF(pr.precio_anual_uf) + '/año';
      return `${formatUF(pr.precio_anual_uf)}/año`;
  }},
  { label: 'Implementación', get: (p) => {
      const pr = p.pricing || {};
      if (pr.moneda_principal === 'CLP') return 'Activación inmediata';
      return pr.implementacion_uf ? formatUF(pr.implementacion_uf) : 'Incluida';
  }},
  { label: 'Contrato 3 años', get: (p) => {
      const pr = p.pricing || {};
      if (pr.compromiso_3a_precio_anual_uf) return `${formatUF(pr.compromiso_3a_precio_anual_uf)}/año`;
      return null;
  }},
  { label: 'IVA', get: (p) => (p.pricing?.iva_incluido ? 'Incluido' : '+ IVA') },

  { section: 'Usuarios y capacidad' },
  { label: 'Instructores', get: (p) => {
      const l = p.limites?.max_instructores;
      if (!l) return '—';
      return l.tipo === 'ilimitado' ? 'Ilimitados' : String(l.valor);
  }},
  { label: 'Colaboradores', get: (p) => {
      const l = p.limites?.max_colaboradores;
      if (!l) return '—';
      return l.tipo === 'ilimitado' ? 'Ilimitados' : String(l.valor);
  }},
  { label: 'Alumnos activos/mes', get: (p) => {
      const l = p.limites?.max_alumnos_activos_mes || p.limites?.max_alumnos_activos_ano || p.limites?.max_alumnos;
      if (!l) return '—';
      if (l.tipo === 'ilimitado') return 'Ilimitados';
      const suffix = p.limites?.max_alumnos_activos_ano ? '/año' : '';
      return `${l.valor}${suffix}`;
  }},
  { label: 'Simulaciones', get: (p) => {
      const pr = p.pricing || {};
      if (pr.simulaciones_incluidas_mes) return `${pr.simulaciones_incluidas_mes}/mes`;
      const l = p.limites?.max_simulaciones_ano || p.limites?.max_simulaciones_mes;
      if (!l) return '—';
      if (l.tipo === 'ilimitado') return 'Ilimitadas';
      const suffix = p.limites?.max_simulaciones_ano ? '/año' : '/mes';
      return `${l.valor.toLocaleString('es-CL')}${suffix}`;
  }},
  { label: 'Escenarios propios', get: (p) => {
      const l = p.limites?.max_escenarios_propios || p.limites?.max_escenarios;
      if (!l) return '—';
      if (l.tipo === 'ilimitado') return 'Ilimitados';
      return String(l.valor);
  }},
  { label: 'Establecimientos / campus', get: (p) => {
      const l = p.limites?.max_establecimientos;
      if (!l) return '—';
      if (l.tipo === 'ilimitado') return 'Ilimitados';
      return l.valor === 0 ? '—' : String(l.valor);
  }},
  { label: 'Sesiones simultáneas', get: (p) => {
      const l = p.limites?.max_simulaciones_simultaneas;
      if (!l) return '—';
      return l.tipo === 'ilimitado' ? 'Ilimitadas' : String(l.valor);
  }},

  { section: 'Add-ons (precio adicional)' },
  { label: 'Instructor extra', get: (p) => {
      const uf = p.pricing?.cobro_usuario_adicional_uf;
      return uf ? `${formatUF(uf)}/año` : null;
  }},
  { label: 'Pack alumnos + simulaciones', get: (p) => {
      const uf = p.pricing?.pack_alumnos_extra_uf;
      return uf ? `${formatUF(uf)}/año` : null;
  }},
  { label: 'Bolsa de simulaciones extra', get: (p) => {
      const uf = p.pricing?.bolsa_simulaciones_uf;
      if (uf) return `${formatUF(uf)} · 1.000 sim`;
      if (p.pricing?.packs_extra?.length) {
        const pack = p.pricing.packs_extra[0];
        return `Desde ${formatCLP(pack.precio_clp)}`;
      }
      return null;
  }},
  { label: 'Escenario personalizado', get: (p) => {
      const uf = p.pricing?.cobro_escenario_personalizado_uf;
      return uf ? `Desde ${formatUF(uf)}` : null;
  }},
  { label: 'Piloto institucional', get: (p) => {
      const uf = p.pricing?.piloto_institucional_uf;
      return uf ? `${formatUF(uf)} · imputable a contrato` : null;
  }},

  { section: 'Servicios y soporte' },
  { label: 'Activación guiada', get: (p) => {
      const code = p.codigo;
      if (code === 'estudiante') return 'Inmediata al pagar';
      if (code === 'starter') return '1 semana';
      if (code === 'profesional') return '2 semanas';
      if (code === 'enterprise') return '4-6 semanas (piloto)';
      if (code === 'red_distribuidor') return '6-10 semanas';
      return true;
  }},
  { label: 'Reuniones de seguimiento', get: (p) => {
      const code = p.codigo;
      if (code === 'starter') return 'Al 1er mes';
      if (code === 'profesional') return '30 días · 90 días · anual';
      if (code === 'enterprise') return 'Mensuales primeros 3 meses + trimestrales';
      if (code === 'red_distribuidor') return 'Cadencia según red';
      return false;
  }},
  { label: 'Debriefing y feedback', get: () => true },
  { label: 'Reportes exportables (PDF/Excel)', get: (p) => p.codigo !== 'estudiante' },
  { label: 'Supervisión en tiempo real', get: (p) => p.codigo !== 'estudiante' },
  { label: 'Gestión multi-programa / multi-campus', get: (p) => ['enterprise', 'red_distribuidor'].includes(p.codigo) },
  { label: 'SLA respuesta', get: (p) => {
      const code = p.codigo;
      if (code === 'estudiante') return 'Centro de ayuda';
      if (code === 'starter') return '2-3 días hábiles';
      if (['profesional', 'enterprise'].includes(code)) return '1 día hábil';
      if (code === 'red_distribuidor') return 'Según acuerdo';
      return false;
  }},
  { label: 'Capacitación in-situ', get: (p) => ['enterprise', 'red_distribuidor'].includes(p.codigo) },
  { label: 'Workshops y jornadas clínicas', get: (p) => ['enterprise', 'red_distribuidor'].includes(p.codigo) ? 'A cotizar' : null },
  { label: 'Manager de alianza dedicado', get: (p) => p.codigo === 'red_distribuidor' },
  { label: 'Facturación consolidada multi-institución', get: (p) => p.codigo === 'red_distribuidor' },
];

const renderCell = (value, isRecommended) => {
  if (value === true) return <Check size={16} className={isRecommended ? 'text-emerald-400' : 'text-cyan-400'} />;
  if (value === false || value === null || value === undefined || value === '—') {
    return <Minus size={14} className="text-slate-600" />;
  }
  return <span className="text-slate-200 text-xs whitespace-pre-line">{value}</span>;
};

export default function PlansComparison({ plans = [] }) {
  const [open, setOpen] = useState(false);
  if (!plans.length) return null;

  return (
    <div className="mt-8">
      <button
        onClick={() => setOpen(!open)}
        data-testid="toggle-plans-comparison"
        className="w-full flex items-center justify-between gap-3 px-6 py-4 bg-slate-800/60 hover:bg-slate-800 border border-slate-700 rounded-2xl transition-all group"
      >
        <div className="flex items-center gap-3 text-left">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
            <Star size={18} className="text-cyan-400" />
          </div>
          <div>
            <div className="text-white font-semibold">Comparar planes en detalle</div>
            <div className="text-slate-400 text-xs">Límites, add-ons y servicios adicionales lado a lado</div>
          </div>
        </div>
        <ChevronDown
          size={20}
          className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="compare-table"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="mt-4 border border-slate-700 rounded-2xl overflow-hidden bg-slate-900/60 backdrop-blur-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[880px]" data-testid="plans-comparison-table">
                  <thead>
                    <tr className="border-b border-slate-700 bg-slate-800/50">
                      <th className="text-left px-4 py-4 text-slate-400 font-medium w-56 sticky left-0 bg-slate-800/50 z-10">
                        Característica
                      </th>
                      {plans.map((p) => (
                        <th
                          key={p.id || p.codigo}
                          className={`px-4 py-4 text-white font-semibold text-center relative ${
                            p.es_recomendado ? 'bg-cyan-500/10' : ''
                          }`}
                        >
                          <div className="text-base">{p.nombre}</div>
                          {p.etiqueta_destacada && (
                            <div className="text-[10px] font-normal text-cyan-400 mt-0.5 uppercase tracking-wide">
                              {p.etiqueta_destacada}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARE_ROWS.map((row, i) => {
                      if (row.section) {
                        return (
                          <tr key={`section-${i}`} className="border-t border-slate-700">
                            <td
                              colSpan={plans.length + 1}
                              className="px-4 py-3 text-xs font-bold uppercase tracking-widest text-cyan-400/80 bg-slate-800/30"
                            >
                              {row.section}
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr
                          key={`row-${i}`}
                          className="border-t border-slate-800/70 hover:bg-slate-800/30 transition-colors"
                        >
                          <td className="px-4 py-3 text-slate-400 sticky left-0 bg-slate-900/60 z-10">
                            {row.label}
                          </td>
                          {plans.map((p) => (
                            <td
                              key={p.id || p.codigo}
                              className={`px-4 py-3 text-center ${p.es_recomendado ? 'bg-cyan-500/5' : ''}`}
                            >
                              <div className="flex items-center justify-center min-h-[24px]">
                                {renderCell(row.get(p), p.es_recomendado)}
                              </div>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-slate-800 px-4 py-3 text-[11px] text-slate-500 text-center">
                Los precios en UF se facturan según el valor UF vigente al momento de la emisión.
                Contrato base anual · Contratos multianuales con descuento adicional · Add-ons se contratan al finalizar el ciclo.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
